import clsx from "clsx";
// MUI
import { Divider, Grid, Typography } from "@mui/material";

// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";
import { formatPhoneNumber } from "@/Common/helpers/Modifiers/formatters";
import { number2ordinal } from "@/Common/helpers/number";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Components
import MetaInformationTitle from "../Skeletons/MetaInformationTitle/MetaInformationTitle";
import FullCollapseContainer from "../Basics/FullCollapseContainer";

// Types
import { Communication, Employee, EmployeeMetaProps, IsCollapsibleProps, MetaData, MetaDataCategories } from "../types";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const getCommunicationValue = (communications: Communication[] = [], type = "", usage = "BUSINESS") => {
	let result = "Unknown";
	if (type) {
		try {
			const info: Communication | undefined = communications.find((item) => type === item.communicationType && usage === item.usage);
			if (info) {
				result = info.value;
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		if (type === "PHONE" && result !== "Unknown") {
			result = formatPhoneNumber(result);
		}
	}
	return result;
};

const IsCollapsible = (props: Readonly<IsCollapsibleProps>) => {
	if (props.isCollapsible) {
		return (
			<FullCollapseContainer
				title={props.data?.name ?? props.data?.fullName ?? ""}
				bottomDivider={props.bottomDivider}
				sectionDivider={props.sectionDivider}
				sxCollapse={props.sxCollapse}
				arrow={props.arrow}
				addons={props.addons}
				roundedTop
				{...props.containerProps}
			>
				{props.children}
			</FullCollapseContainer>
		);
	} else {
		return (
			<>
				<Grid container>
					<Grid item xs={12}>
						<Typography className="!font-montserrat !text-[24px]/[1.2] !text-default !font-medium">
							{props.data?.fullName}
						</Typography>
						<Divider
							className="!border-disabled relative -left-[32px] !mt-[24px]"
							variant="fullWidth"
							sx={{ width: "calc(100% + 64px)" }}
						/>
					</Grid>
				</Grid>
				{props.children}
			</>
		);
	}
};

export default function EmployeeMeta(props: Readonly<EmployeeMetaProps>) {
	const columns = props.secondaryColumn ? [...props.columns, props.secondaryColumn] : props.columns;

	return (
		<>
			{props.loading ? (
				<MetaInformationTitle addons={Boolean(props.addons)} />
			) : (
				<>
					{Boolean(props.data) && (
						// TODO: find a way to remove the sx prop.
						// So far it's not possible to set "flex-wrap: inherit" using Tailwind utility classes.
						<Grid container direction="column" sx={{ flexWrap: "inherit" }}>
							<IsCollapsible {...props}>
								<Grid item className="!mt-[10px] pb-[10px] !mr-[45px]">
									<Grid
										container
										direction="row"
										className={clsx({
											"py-[16px]": true,
											"ml-[32px]": props.isCollapsible,
											"ml-[2px]": !props.isCollapsible,
										})}
									>
										{columns?.map((column, index) => {
											index += 1;
											const isLastColumn = index === columns?.length;
											return (
												<Grid
													item
													className={clsx({
														"pr-[50px]": (props.isCollapsible && !isLastColumn) || !props.isCollapsible,
														"max-w-[450px]": props.isCollapsible && !isLastColumn,
														"max-w-[200px]": !(props.isCollapsible && !isLastColumn),
														"px-[1.5rem] border-l border-solid border-[#CCC]":
															props.secondaryColumn?.length && isLastColumn,
														"max-w-[400px]": !props.isCollapsible,
													})}
													key={String(column)}
												>
													<Grid container direction="column">
														{column?.map((metaData, index2) => {
															const data = getMetaData(props.data, metaData);
															const key = `${metaData}-${index2}`;
															return (
																<Grid item key={key}>
																	<Grid container direction="row">
																		<Grid item>
																			<Typography className="!font-montserrat !font-medium text-[color:--neutral-gray] !text-[16px]">
																				{data.title}:&nbsp;
																			</Typography>
																		</Grid>
																		<Grid item>
																			{data.title === "Ranking" ? (
																				<Typography
																					className="!font-montserrat !font-medium text-[color:--text-color] !text-[16px] max-w-[55px]"
																					noWrap
																				>
																					{typeof data.value === "number"
																						? number2ordinal(data.value)
																						: data.value}
																				</Typography>
																			) : (
																				<Typography className="!font-montserrat !font-medium text-[color:--text-color] !text-[16px]">
																					{data.value}
																				</Typography>
																			)}
																		</Grid>
																	</Grid>
																</Grid>
															);
														})}
													</Grid>
												</Grid>
											);
										})}
									</Grid>
								</Grid>
							</IsCollapsible>
						</Grid>
					)}
				</>
			)}
		</>
	);
}

function getFirstColumnMeta(employee?: Employee, metaData?: MetaDataCategories) {
	switch (metaData) {
		case "POSITION":
			return {
				title: "Position",
				value: employee?.position ?? "",
			};
		case "DEPARTMENT":
			return {
				title: "Department",
				value: employee?.department ?? "",
			};
		default:
			return {
				title: "Reports to",
				value: employee?.reportsTo ?? "",
			};
	}
}

function getSecondColumnMeta(employee?: Employee, metaData?: MetaDataCategories) {
	switch (metaData) {
		case "ID":
			return {
				title: "Employee ID",
				value: employee?.employeeId ?? "",
			};
		case "EMAIL":
			return {
				title: "Email",
				value: getCommunicationValue(employee?.communications, "EMAIL"),
			};
		default:
			return {
				title: "Phone number",
				value: getCommunicationValue(employee?.communications, "PHONE", "BUSINESS"),
			};
	}
}

function getThirdColumnMeta(employee?: Employee, metaData?: MetaDataCategories) {
	switch (metaData) {
		case "PAY_DATE":
			return {
				title: "Pay Date",
				value: getFormattedDate(employee?.payDate ?? "", "UTC", DATE_FORMAT.monthAbbrDatePadded) ?? "",
			};
		case "PAY_RANGE":
			return {
				title: "Pay Range",
				value: `${getFormattedDate(employee?.payRange?.startDate ?? "", "UTC", DATE_FORMAT.monthDayAbbrPadded) ?? ""} - ${
					getFormattedDate(employee?.payRange?.endDate ?? "", "UTC", DATE_FORMAT.monthDayAbbrPadded) ?? ""
				}`,
			};
		default:
			return {
				title: "Store",
				value: employee?.store ?? "",
			};
	}
}

const getMetaData = (employee?: Employee, metaData?: MetaDataCategories): MetaData => {
	if (metaData && ["POSITION", "DEPARTMENT", "REPORTS_TO"].includes(metaData)) {
		return getFirstColumnMeta(employee, metaData);
	}
	if (metaData && ["ID", "EMAIL", "PHONE"].includes(metaData)) {
		return getSecondColumnMeta(employee, metaData);
	}
	if (metaData && ["PAY_DATE", "PAY_RANGE", "STORE"].includes(metaData)) {
		return getThirdColumnMeta(employee, metaData);
	}
	switch (metaData) {
		case "DAYS_OUT":
			return {
				title: "Days Out",
				value: employee?.daysOut ?? "",
			};
		case "RANKING":
			return {
				title: "Ranking",
				value: employee?.ranking ?? "",
			};

		default:
			return {
				title: "",
				value: "",
			};
	}
};
